import type { HandleClientError } from "@sveltejs/kit";
import * as Sentry from "@sentry/sveltekit";
import { reportClientError } from "$lib/utils/reportClientError";
import { get } from "svelte/store";
import { setAnalyticsConsent } from "$lib/utils/analytics";
import { analyticsConsent } from "$lib/stores/cookieNoticeStore";

// Mirrors plotcrate-api's gating (plotcrate_api/app/observability.py):
// Sentry initializes only in a deployed environment with a DSN, never for
// local dev or CI/test runs. This is a static SPA build (adapter-static, no
// SvelteKit server at runtime — see +layout.svelte), so only the client SDK
// is wired up; there is no hooks.server.ts to instrument.
//
// VITE_SENTRY_DSN and VITE_SENTRY_ENVIRONMENT are Vite env vars baked in at
// build time: .env.production sets "production", .env.dev sets "dev", both
// with the shared plotcrate project's DSN. .env and the local-mode files set
// neither, so local builds never initialize Sentry.
const SENTRY_ENVIRONMENTS = new Set(["production", "dev"]);
const dsn = import.meta.env.VITE_SENTRY_DSN;
const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT;

if (dsn && environment && SENTRY_ENVIRONMENTS.has(environment)) {
   Sentry.init({
      dsn,
      environment,
      // No performance/session-replay sampling configured yet — this wiring
      // exists to make crashes visible, not to add tracing overhead.
      tracesSampleRate: 0,
      initialScope: { tags: { service: "frontend" } },
   });
}

setAnalyticsConsent(get(analyticsConsent) === "granted");

export const handleError: HandleClientError = ({ error, event }) => {
   reportClientError(error, { url: event.url.pathname });

   const message =
      error instanceof Error ? error.message : "An unexpected error occurred";

   return { message };
};
